import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { achievements, badgeIcon, type Badge } from './api';
import { BadgesDialog } from './BadgesDialog';

/**
 * The badges somebody has, on their profile.
 *
 * ## A row of artwork, not a list
 *
 * These are illustrations and they are the point - a wall of text saying
 * "Explorer, Early Ears, First Drop" throws away the only thing that makes a
 * badge feel like one. So the shelf is the pictures at a readable size, and
 * everything else is one click away.
 *
 * ## It renders nothing rather than an empty shelf
 *
 * Somebody who has earned none should not have a section announcing that. The
 * exception is your own profile: an empty shelf you can open is how you find
 * out badges exist at all, so yours says so and offers the list.
 */
export function BadgeShelf({ username }: { username: string }) {
  const [open, setOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['achievements', username],
    queryFn: () => achievements.forUser(username),
  });

  if (!data) return null;

  const earned = data.items.filter((badge) => badge.earned);

  if (earned.length === 0 && !data.mine) return null;

  return (
    <div className="mt-7 flex flex-col gap-3" data-badge-shelf>
      <div className="flex items-baseline justify-between gap-3">
        <span className="meta-caps text-faint">badges</span>
        <button
          type="button"
          data-badges-open
          onClick={() => setOpen(true)}
          className="meta-caps text-faint underline decoration-line underline-offset-4 transition-colors hover:text-soft"
        >
          {earned.length} of {data.total} →
        </button>
      </div>

      {earned.length === 0 ? (
        <button
          type="button"
          data-badges-open
          onClick={() => setOpen(true)}
          className="rounded-[var(--radius-card)] border border-dashed border-line px-4 py-5 text-left text-sm text-soft transition-colors hover:border-line-strong hover:text-ink"
        >
          No badges yet — see what there is to earn.
        </button>
      ) : (
        <div className="flex flex-wrap items-start gap-4">
          {earned.map((badge) => (
            <button
              key={badge.slug}
              type="button"
              data-badge={badge.slug}
              title={`${badge.label} — ${badge.blurb}`}
              onClick={() => setOpen(true)}
              className="group flex w-[86px] flex-col items-center gap-1.5"
            >
              <img
                src={badgeIcon(badge.icon.small)}
                alt=""
                width={64}
                height={64}
                // Lifted rather than glowed on hover. The artwork already has
                // its own light in it, and a second one underneath turns a
                // badge into a button.
                className="h-16 w-16 transition-transform group-hover:-translate-y-0.5"
              />
              <span className="text-center font-mono text-[9px] uppercase leading-tight tracking-[0.08em] text-faint transition-colors group-hover:text-soft">
                {badge.label}
              </span>
            </button>
          ))}
        </div>
      )}

      {open && <BadgesDialog list={data} onClose={() => setOpen(false)} />}
    </div>
  );
}

/** The one figure a locked badge is allowed to show. */
export function badgeProgressLabel(badge: Badge): string {
  return `${badge.progress.toLocaleString()} / ${badge.target.toLocaleString()}`;
}
